"use client";

import { useState } from "react";
import { useSnapshot } from "valtio";

import { validateInviteCode } from "@features/invite/validateInviteCode";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { isValidInviteCodeFormat } from "@utils/invite/inviteCode";
import { applyManualInviteCode } from "@utils/invite/inviteCodeCookie.client";
import {
  inviteCodeState,
  setInviteCodeAppliedInStore,
} from "@utils/invite/inviteCodeStore";

/**
 * 邀请码手动输入的**唯一逻辑源** —— 两个呈现壳共用：
 *
 * - PC：{@link import("./InviteCodeSection").InviteCodeSection} 折叠式就地输入
 * - H5：{@link import("./InviteCodeStageView").InviteCodeStageView} 全屏页（对齐原生 `InviteCodeStage`）
 *
 * 呈现分端是被屏幕尺寸决定的（PC 垂直空间充裕可就地展开；移动端不够，且 `SignInPageLayout`
 * 是 `h-dvh + overflow-hidden` 无滚动容器，键盘一弹内容会被裁掉），但**判定与落盘口径必须一份**，
 * 否则两端会在「什么算无效」「判不了要不要放行」上漂移。
 *
 * 原生 KMP 的 `InviteCodeStage.handleApply` 是同口径的 Kotlin 实现（无法共享代码，
 * 靠此处注释与同名埋点 label 对齐）。
 */
export interface UseInviteCodeApplyOptions {
  /** 提交成功（valid 或判不了）后调用 —— PC 用来收起折叠，H5 用来退回邮箱步骤。 */
  onDone: () => void;
}

export interface UseInviteCodeApplyResult {
  /** 当前输入值 */
  value: string;
  /** 输入变化（会顺带清掉错误态） */
  onValueChange: (next: string) => void;
  /** 是否展示错误态（格式非法 / 后端判无效） */
  error: boolean;
  /** 远程校验进行中 */
  checking: boolean;
  /** 是否允许提交（空值或校验中不允许） */
  canSubmit: boolean;
  /** 提交 */
  apply: () => Promise<void>;
  /** 把已捕获的码灌进输入框（进入编辑态时调用） */
  reset: () => void;
}

export function useInviteCodeApply({
  onDone,
}: UseInviteCodeApplyOptions): UseInviteCodeApplyResult {
  const { code: capturedCode } = useSnapshot(inviteCodeState);
  const { memberApi } = useOspApiClientProvider();

  // 预填已捕获的码（从邀请链接进来的场景），用户可改
  const [value, setValue] = useState(capturedCode ?? "");
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  const onValueChange = (next: string) => {
    setValue(next);
    // 用户重新输入即清错，避免错误态一直挂着
    if (error) {
      setError(false);
    }
  };

  const reset = () => {
    setValue(inviteCodeState.code ?? "");
    setError(false);
  };

  const apply = async () => {
    // ① 格式兜底：本地就能判死的，不必打网络（也省限流额度）
    if (!isValidInviteCodeFormat(value)) {
      setError(true);
      return;
    }
    // ② 远程校验：`GET /auth/validate`，与注册链路共用后端 InviteCodeResolver，口径一致
    setChecking(true);
    const result = await validateInviteCode(memberApi, value);
    setChecking(false);

    // 用户**主动输入**的码判定为无效 → 就地报错让他当场改（这是纠错价值最高的时刻）
    if (result.status === "invalid") {
      setError(true);
      return;
    }
    // valid 与 unknown 都放行，不把用户卡住：
    // unknown = 429 限流 / 网络失败，判定不了不等于无效，改码也解决不了。
    // 收尾后那一行会按 applied 标记展示 `applied` / `not applied`。
    applyManualInviteCode(value);
    setInviteCodeAppliedInStore(result.status === "valid");
    onDone();
  };

  return {
    value,
    onValueChange,
    error,
    checking,
    canSubmit: value.trim().length > 0 && !checking,
    apply,
    reset,
  };
}
